<template>
  <div>
    <router-view v-if="isNestedRouteActive" />
    <div v-else class="py-8 px-2 px-md-16">
      <Header />
      <div class="staking-plan">
        <Tabs @setActiveTab="setActiveTab" :active-tab="activeTab" />
        <Filters
          :active-tab="activeTab"
          :active-filter="activeFilter"
          :active-show="activeShow"
          :compare-asset="payload.AssetCompare"
          @setActiveFilter="setActiveFilter"
          @setActiveShow="setActiveShow"
          @setCompareAsset="setCompareAsset"
          @searchHandler="debounceSearch"
        />
        <v-skeleton-loader
          type="table-tbody"
          width="100%"
          :loading="isLoading"
          class="custom-loading mt-8 gray-10"
        >
          <Plans
            v-if="subscribePlans.length > 0"
            :active-tab="activeTab"
            :active-filter="activeFilter"
            :active-show="activeShow"
            :plans="subscribePlans"
          />
          <div
            v-else
            class="d-flex flex-column align-center justify-center text-center py-16"
          >
            <v-icon style="width: 50px;height: 50px">$paper</v-icon>
            <p class="font-Medium-24 onPrimary--text pt-4">
              {{ $t("staking.stakingPlans.No_Plans_Found") }}
            </p>
            <p class="font-Light-16 gray2--text">
              {{ $t("staking.stakingPlans.No_Plans_For_This_Condition") }}
            </p>
          </div>
          <v-btn
            v-if="subscribePlans.length > 0 && !isLastPage"
            @click="showMore"
            id="showMore"
            color="primary"
            class="py-4 my-8 d-flex align-center justify-center mx-auto showMore"
            >{{ $t("staking.stakingPlans.View_More") }}</v-btn
          >
        </v-skeleton-loader>
      </div>
      <Calculator v-if="activeTab === ShowPlansType.ALL_PLANS" />
      <StakingFeatures />
      <Banner />
      <FaqSection id="faqSection" />
    </div>
  </div>
</template>

<script>
import Header from "@/components/Staking/MainPage/Header.vue";
import Calculator from "@/components/Staking/MainPage/Calculator.vue";
import StakingFeatures from "@/components/Staking/MainPage/Features.vue";
import Banner from "@/components/Staking/MainPage/Banner.vue";
import FaqSection from "@/components/Shared/FaqSection/index.vue";
import debounce from "lodash-es/debounce";
import StakingService from "@/services/modules/market/staking";
import Plans from "@/components/Staking/MainPage/StakingPlans/Plans.vue";
import Tabs from "@/components/Staking/MainPage/StakingPlans/Tabs.vue";
import Filters from "@/components/Staking/MainPage/StakingPlans/Filters.vue";
// vuex
import {
  FiltersType,
  Property,
  ShowPlansType,
  SortDirection,
  staking,
  TYPE_VALUE,
  VIPStatus
} from "@/store/types";
import { mapActions, mapGetters } from "vuex";
import { authMixin } from "@/mixins/authMixin";

export default {
  mixins: [authMixin],
  components: {
    Filters,
    Tabs,
    Plans,
    Banner,
    StakingFeatures,
    Calculator,
    Header,
    FaqSection
  },
  data() {
    return {
      activeTab: ShowPlansType.ALL_PLANS, //ALL_PLANS or MY_PLANS
      activeFilter: null,
      activeShow: FiltersType.ALL,
      isLoading: false,
      isLastPage: false,
      payload: {
        pageNo: 1,
        pageSize: 16,
        isVIP: null, //IS_TRUE or IS_FALSE
        property: null, //duration or activeAPY
        direction: null, //ASC or DESC
        coinUnit: null,
        AssetCompare: null,
        apyType: null
      }
    };
  },
  mounted() {
    this.setInitQuery();
  },
  methods: {
    ...mapActions({
      setSubscribePlans: staking.actions.SET_SUBSCRIBE_PLANS
    }),

    setInitQuery() {
      if (this.$route.params.id) {
        this.$router.push({ query: "" });
        return;
      }
      if (this.isAuth) {
        if (this.$route.query.tab) {
          this.setActiveTab(this.$route.query.tab);
        } else {
          this.setActiveTab(this.ShowPlansType.ALL_PLANS);
        }
      } else {
        this.setActiveTab(this.ShowPlansType.ALL_PLANS);
      }
    },

    async fetchPlans({ isMyPlans = false, append = false } = {}) {
      if (!append) this.isLoading = true;
      if (!append) this.payload.pageNo = 1;

      try {
        const payload = isMyPlans
          ? {
              pageNo: this.payload.pageNo,
              pageSize: this.payload.pageSize,
              status: this.activeFilter || FiltersType.ACTIVE
            }
          : { ...this.payload };

        let serviceMethod;
        if (!this.isAuth) {
          serviceMethod = StakingService.getPublicPlans;
        }
        if (this.isAuth) {
          serviceMethod = isMyPlans
            ? StakingService.getMyStakingPlans
            : StakingService.getAllPlans;
        }

        const res = await serviceMethod(payload);
        const plans = res.data.data.content;
        const isLastPage = res.data.data.last;

        await this.setSubscribePlans({ payload: plans, append: append });
        this.isLastPage = isLastPage;
      } finally {
        this.isLoading = false;
      }
    },
    setActiveTab(value) {
      this.activeTab = value;
      this.$router.push({
        query: { tab: value }
      });
      if (value === ShowPlansType.MY_PLANS) {
        this.activeFilter = FiltersType.ACTIVE;
        this.fetchPlans({ isMyPlans: true });
      }
      if (value === ShowPlansType.ALL_PLANS) {
        this.resetFilters();
        this.resetPayload();
        this.fetchPlans();
      }
    },
    setActiveFilter(value) {
      this.activeFilter = value;
      switch (value) {
        case FiltersType.MAX_PROFIT:
          this.setSort(Property.ACTIVE_APY, SortDirection.DESC);
          break;
        case FiltersType.MIN_PROFIT:
          this.setSort(Property.ACTIVE_APY, SortDirection.ASC);
          break;
        case FiltersType.MAX_TIME:
          this.setSort(Property.DURATION, SortDirection.DESC);
          break;
        case FiltersType.MIN_TIME:
          this.setSort(Property.DURATION, SortDirection.ASC);
          break;
        case FiltersType.FIX:
          this.setApyType(TYPE_VALUE.FIX);
          break;
        case FiltersType.FLEXIBLE:
          this.setApyType(TYPE_VALUE.FLEXIBLE);
          break;
        default:
          this.setSort(null, null);
          this.setApyType(null);
      }

      if (this.activeTab === ShowPlansType.MY_PLANS && this.activeFilter === "")
        this.activeFilter = FiltersType.ACTIVE;

      this.fetchPlans({ isMyPlans: this.activeTab === ShowPlansType.MY_PLANS });
    },
    setActiveShow(value) {
      this.payload.isVIP = value === FiltersType.VIP ? VIPStatus.TRUE : null;
      this.activeShow = value;
      this.fetchPlans();
    },
    setCompareAsset() {
      this.payload.AssetCompare = !this.payload.AssetCompare;
      this.fetchPlans();
    },
    debounceSearch: debounce(function(e) {
      this.searchHandler(e);
    }, 500),
    searchHandler(e) {
      const value = e.target.value;
      this.payload.coinUnit = value;
      this.fetchPlans();
    },
    showMore() {
      this.payload.pageNo++;
      this.fetchPlans({
        isMyPlans: this.activeTab === ShowPlansType.MY_PLANS,
        append: true
      });
    },
    resetPayload() {
      this.payload = {
        pageNo: 1,
        pageSize: 16,
        isVIP: null,
        property: null,
        direction: null,
        coinUnit: null,
        AssetCompare: null
      };
    },
    resetFilters() {
      this.activeTab = ShowPlansType.ALL_PLANS;
      this.activeFilter = null;
      this.activeShow = FiltersType.ALL;
    },
    setSort(property, direction) {
      this.payload = {
        ...this.payload,
        property,
        direction
      };
    },
    setApyType(value) {
      this.payload = {
        ...this.payload,
        apyType: value
      };
    }
  },
  computed: {
    ...mapGetters({
      subscribePlans: staking.getters.GET_SUBSCRIBE_PLANS,
      calculatorPlans: staking.getters.GET_CALCULATOR_PLANS
    }),
    ShowPlansType() {
      return ShowPlansType;
    },
    isNestedRouteActive() {
      return this.$route.params.id;
    }
  }
};
</script>

<style lang="scss">
.custom-loading {
  .v-skeleton-loader__table-tbody {
    background-color: transparent !important;
  }
  .v-skeleton-loader__table-row {
    height: 80px !important;
    margin-bottom: 16px !important;
  }
  .v-skeleton-loader__table-row {
    background-color: var(--v-gray10-base) !important;
    padding: 16px !important;
  }
}
</style>
